import React, { useState, useEffect, useRef } from 'react';
import { useHistory } from "react-router-dom";
import { useParams } from 'react-router-dom';
import axios from '../../../../axios-order'
import {
    CircularProgress, RadioGroup, Radio, FormControlLabel, Box, Tab, Paper, Tabs, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, TablePagination, Select, MenuItem,
    FormControl, InputLabel, Typography, Button, IconButton, TextField, Popover, Tooltip,
    Dialog, DialogTitle, DialogContent, DialogActions, Chip
} from '@mui/material';
import { LoadingButton, TabContext } from '@mui/lab';
import { styled } from '@mui/material/styles';
import BubbleChartIcon from '@mui/icons-material/BubbleChart';
import EditCalendarIcon from '@mui/icons-material/EditCalendar';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import SaveIcon from '@mui/icons-material/Save';
import Snackbar from "@material-ui/core/Snackbar";
import MuiAlert from "@material-ui/lab/Alert";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import SpaceDashboardIcon from '@mui/icons-material/SpaceDashboard';
import RefreshIcon from '@mui/icons-material/Refresh';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { useLocation } from 'react-router-dom';
import { canRead, canWrite } from "../../../../utils/privilegeUtils";

// ---- Ledger theme tokens (v2 — refreshed, neutral/indigo system) ----
const palette = {
    primary: '#4F46E5',
    primaryHover: '#4338CA',
    primarySoft: '#EEF2FF',
    primaryBorder: '#C7D2FE',
    ink: '#0F172A',
    inkSoft: '#1E293B',
    slateSoft: '#F8FAFC',
    card: '#FFFFFF',
    border: '#E2E8F0',
    borderStrong: '#CBD5E1',
    text: '#1E293B',
    textMuted: '#64748B',
    textFaint: '#94A3B8',
    amber: '#D97706',
    amberSoft: '#FFFBEB',
    amberBorder: '#FDE68A',
    danger: '#DC2626',
    dangerSoft: '#FEF2F2',
    dangerBorder: '#FECACA',
    success: '#16A34A',
    successSoft: '#F0FDF4',
    successBorder: '#BBF7D0',

    // ---- Header banner gradient ----
    headerGradientStart: '#183770',   // deep navy
    headerGradientEnd: '#232232',     // dark slate-plum

    tableHeaderBg: '#2F5FBD',
    tableHeaderBgSoft: '#3B6FD1',
    tableHeaderText: '#FFFFFF',
    tableHeaderAccent: '#8AB4FF',
    tableBodyBg: '#FFFFFF',
    tableBodyAltBg: '#F4F7FD',
    tableBodyHover: '#EAF0FC',
    tableBorder: '#E1E8F5',

    // Cyclic label colors — mirrors the reference design's rotating pill colors
    labelCycle: ['#2F5FBD', '#0EA5A0', '#7C3AED', '#EA8C2B', '#0EA5E9', '#DB2777'],
};

// Purely presentational — maps a status string to a chip color. No business logic involved.
const getStatusChipStyle = (rawValue) => {
    const v = (rawValue || '').toString().toLowerCase();
    if (!v) return { bg: '#F1F5F9', text: palette.textMuted, border: palette.border, dot: palette.textFaint };
    if (v.includes('mismatch') || v.includes('fail') || v.includes('unavail') || v.includes('ineligible')) {
        return { bg: palette.dangerSoft, text: palette.danger, border: palette.dangerBorder, dot: palette.danger };
    }
    if (v.includes('match') || v.includes('avail') || v.includes('pass') || v.includes('eligible')) {
        return { bg: palette.successSoft, text: palette.success, border: palette.successBorder, dot: palette.success };
    }
    if (v.includes('pending') || v.includes('progress') || v.includes('missing')) {
        return { bg: palette.amberSoft, text: palette.amber, border: palette.amberBorder, dot: palette.amber };
    }
    return { bg: palette.primarySoft, text: palette.primary, border: palette.primaryBorder, dot: palette.primary };
};

const StatusChip = ({ value }) => {
    const s = getStatusChipStyle(value);
    if (!value) return <span style={{ color: palette.textFaint, fontSize: '12.5px' }}>—</span>;
    return (
        <Chip
            size="small"
            label={value}
            icon={
                <span
                    style={{
                        display: 'inline-block',
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        backgroundColor: s.dot,
                        marginLeft: '8px',
                    }}
                />
            }
            sx={{
                height: '22px',
                fontSize: '11px',
                fontWeight: 600,
                letterSpacing: '0.2px',
                backgroundColor: s.bg,
                color: s.text,
                border: `1px solid ${s.border}`,
                borderRadius: '999px',
                '& .MuiChip-icon': { order: 1, marginRight: '4px', marginLeft: '-2px' },
                '& .MuiChip-label': { paddingLeft: '6px', paddingRight: '8px' },
            }}
        />
    );
};

const BpIcon = styled('span')(({ theme }) => ({
    borderRadius: '50%',
    width: 14,
    height: 14,
    boxShadow: `inset 0 0 0 1px ${palette.border}`,
    backgroundColor: '#ffffff',
    '.Mui-focusVisible &': {
        outline: `2px auto ${palette.primary}`,
        outlineOffset: 2,
    },
    'input:hover ~ &': {
        backgroundColor: palette.primarySoft,
    },
    'input:disabled ~ &': {
        boxShadow: 'none',
        background: 'rgba(206,217,224,.5)',
    },
}));

const BpCheckedIcon = styled(BpIcon)({
    backgroundColor: palette.primary,
    boxShadow: `inset 0 0 0 1px ${palette.primary}`,
    '&::before': {
        display: 'block',
        width: 14,
        height: 14,
        backgroundImage: 'radial-gradient(#fff,#fff 28%,transparent 32%)',
        content: '""',
    },
    'input:hover ~ &': {
        backgroundColor: palette.primaryHover,
    },
});


function BpRadio(props) {
    return (
        <Radio
            disableRipple
            color="default"
            checkedIcon={<BpCheckedIcon />}
            icon={<BpIcon />}
            {...props}
        />
    );
}

const GSTComputationReportDetails = () => {
    let history = useHistory();
    const isMounted = useRef(false);
    const { reconId } = useParams();
    const [value, setValue] = useState('Matched');
    const [data, setData] = useState([]);
    const [taxPeriod, setTaxPeriod] = useState('')
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalRows, setTotalRows] = useState(3);
    const [selectedOption, setSelectedOption] = useState('ITC');
    const [loading, setLoading] = useState(false);
    const [advancedFilter, setAdvancedFilter] = useState(false);
    const [saveButtonLoading, setSaveButtonLoading] = useState(false)
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarSeverity, setSnackbarSeverity] = useState("success");
    const [message, setMessage] = useState('');
    const [responseMessage, setResponseMessage] = useState("");
    const [isSavedItc, setIsSaveItc] = useState(false)
    const [isSavedRcm, setIsSavedRcm] = useState(false)
    const [snackCloseTime, setSnackCloseTime] = useState(5000)
    // Compute button is enabled only after a successful (200) save
    const [isComputeEnabled, setIsComputeEnabled] = useState(false);
    const location = useLocation();
    const { financial_year, period } = location.state || {};

    // Hide ITC Utilized Month + Reversal columns only on ITC → Mismatch
    const showReversalColumns = !(selectedOption === 'ITC' && value === 'Mismatch');

    // State for filter values and conditions
    const [anchorElGstin, setAnchorElGstin] = useState(null);
    const [anchorElLegalName, setAnchorElLegalName] = useState(null);
    const [anchorElDocumentNo, setAnchorElDocumentNo] = useState(null);
    const [anchorElMatchResult, setAnchorElMatchResult] = useState(null);
    const [filterValueGstin, setFilterValueGstin] = useState('');
    const [filterValueLegalName, setFilterValueLegalName] = useState('');
    const [filterValueDocumentNo, setFilterValueDocumentNo] = useState('');
    const [filterValueMatchResult, setFilterValueMatchResult] = useState('');

    const [exportButtonLaoding, setExportButtonLaoding] = useState(false)
    const [isChngesNotSaved, satIsChnagesNotSaved] = useState(false)

    const [itcUtilizedMonthOptions, setItcUtilizedMonthOptions] = useState([])

    // ---- Bulk Export states ----
    const [bulkExportOpen, setBulkExportOpen] = useState(false);
    const [bulkUploadFile, setBulkUploadFile] = useState(null);
    const [bulkUploadLoading, setBulkUploadLoading] = useState(false);
    const [showBulkLogs, setShowBulkLogs] = useState(false);
    const [bulkExportLogs, setBulkExportLogs] = useState([]);
    const [bulkLogsLoading, setBulkLogsLoading] = useState(false);
    const [bulkDragActive, setBulkDragActive] = useState(false);
    const [bulkTemplateLoading, setBulkTemplateLoading] = useState(false);


    const [downloadingLogId, setDownloadingLogId] = useState(null);


    useEffect(() => {
        if (isMounted.current) {
            if (isChngesNotSaved) {
                handleItcDataSave()
            }
            setData([]);
            setPage(0);
            setTotalRows(0);
            setRowsPerPage(10);
            getGstComputationDetailedData();
        } else {
            isMounted.current = true;
        }
    }, [value, selectedOption, advancedFilter]);

    useEffect(() => {
        if (isChngesNotSaved) {
            handleItcDataSave()
        }
        getGstComputationDetailedData()
    }, [page, rowsPerPage, filterValueGstin, filterValueLegalName, filterValueDocumentNo, filterValueMatchResult])

    const handleFilterIconClick = (column, event) => {
        if (column === 'gstin') setAnchorElGstin(event.currentTarget);
        if (column === 'legalName') setAnchorElLegalName(event.currentTarget);
        if (column === 'documentNo') setAnchorElDocumentNo(event.currentTarget);
        if (column === 'matchResult') setAnchorElMatchResult(event.currentTarget);
    };

    const handlePopoverClose = (column) => {
        if (column === 'gstin') setAnchorElGstin(null);
        if (column === 'legalName') setAnchorElLegalName(null);
        if (column === 'documentNo') setAnchorElDocumentNo(null);
        if (column === 'matchResult') setAnchorElMatchResult(null);
    };

    const handleFilterValueChange = (column, e) => {
        if (column === 'gstin') setFilterValueGstin(e.target.value);
        if (column === 'legalName') setFilterValueLegalName(e.target.value);
        if (column === 'documentNo') setFilterValueDocumentNo(e.target.value);
        if (column === 'matchResult') setFilterValueMatchResult(e.target.value);
    };


    const handleSnackbarOpen = (message, severity) => {
        setSnackbarSeverity(severity);
        setResponseMessage(message);
        setSnackbarOpen(true);
    };

    // Function to close Snackbar
    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
        setSnackbarSeverity(snackbarSeverity);
        setResponseMessage("");
    };


    const handleChangeTab = (event, newValue) => {
        setValue(newValue);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(+event.target.value);
        setPage(0);
    };

    const handleSelectChange = (event) => {
        setSelectedOption(event.target.value);
    };

    const handleItcMonthChange = (event, rowIndex) => {
        satIsChnagesNotSaved(true)
        setIsComputeEnabled(false)
        const updatedRows = [...data];
        updatedRows[rowIndex].itc_utilized_month = event.target.value;
        setData(updatedRows);

        // If month is None/empty and reversal is Permanent Rev., throw error
        if ((event.target.value === 'None' || !event.target.value) && updatedRows[rowIndex].perm_and_temp_rev === 'Permanent Rev.') {
            handleSnackbarOpen('ITC Utilized Month cannot be None when Reversal is Permanent Rev.', 'warning');
        }

        // If month is selected and reversal is Temporary Rev., throw error
        if (event.target.value && event.target.value !== 'None' && updatedRows[rowIndex].perm_and_temp_rev === 'Temporary Rev.') {
            handleSnackbarOpen('ITC Utilized Month should be None when Reversal is Temporary Rev.', 'warning');
        }
    };

    // const handleItcMonthChange = (event, rowIndex) => {
    //     satIsChnagesNotSaved(true)
    //     const updatedRows = [...data];
    //     updatedRows[rowIndex].itc_utilized_month = event.target.value;
    //     setData(updatedRows);
    // };

    // const handleReversalChange = (event, rowIndex) => {
    //     const selectedValue = event.target.value;
    //     const row = data[rowIndex];

    //     if (selectedValue === 'Permanent Rev.' && (!row.itc_utilized_month || row.itc_utilized_month === 'None')) {
    //         handleSnackbarOpen('Please select ITC Utilized Month before selecting Permanent Reversal.', 'warning');
    //         return;
    //     }

    //     satIsChnagesNotSaved(true);
    //     const updatedRows = [...data];
    //     updatedRows[rowIndex].perm_and_temp_rev = selectedValue;
    //     setData(updatedRows);
    // };

    const handleReversalChange = (event, rowIndex) => {
        const selectedValue = event.target.value;
        const row = data[rowIndex];

        // If Permanent Rev. and month is None/empty, throw error
        if (selectedValue === 'Permanent Rev.' && (!row.itc_utilized_month || row.itc_utilized_month === 'None')) {
            handleSnackbarOpen('Please select ITC Utilized Month before selecting Permanent Reversal.', 'warning');
            return;
        }

        // If Temporary Rev. and month is selected, throw error
        if (selectedValue === 'Temporary Rev.' && row.itc_utilized_month && row.itc_utilized_month !== 'None') {
            handleSnackbarOpen('ITC Utilized Month must be None when selecting Temporary Reversal.', 'warning');
            return;
        }

        satIsChnagesNotSaved(true);
        setIsComputeEnabled(false);
        const updatedRows = [...data];
        updatedRows[rowIndex].perm_and_temp_rev = selectedValue;
        setData(updatedRows);
    };


    const validateReversalData = () => {
        for (let i = 0; i < data.length; i++) {
            const row = data[i];
            const month = row.itc_utilized_month;
            const reversal = row.perm_and_temp_rev;

            if (reversal === 'Permanent Rev.' && (!month || month === 'None')) {
                handleSnackbarOpen(`Row ${i + 1}: Please select ITC Utilized Month before selecting Permanent Reversal.`, 'warning');
                return false;
            }

            if (reversal === 'Temporary Rev.' && month && month !== 'None') {
                handleSnackbarOpen(`Row ${i + 1}: ITC Utilized Month must be None when selecting Temporary Reversal.`, 'warning');
                return false;
            }
        }
        return true;
    };

    const handleComputationReport = () => {
        setSnackCloseTime(5000);

        // If there are unsaved changes, validate + save first, then navigate
        if (isChngesNotSaved) {
            if (!validateReversalData()) return;

            setSaveButtonLoading(true);
            const payLoadData = {
                business_entity_id: localStorage.getItem('business_entity_id'),
                summary_id: reconId,
                records: data,
                is_changed: isChngesNotSaved,
                financial_year: financial_year || null,
                period: period || null,
      };

            axios.post('/save_itc_utilized_records', payLoadData)
                .then((res) => {
                    if (res.data.status) {
                        satIsChnagesNotSaved(false);
                        history.push({
                            pathname: `/gst_computation_report_details_data/${reconId}`
                        });
                    } else {
                        handleSnackbarOpen('Save failed. Please try again.', 'error');
                    }
                })
                .catch(() => {
                    handleSnackbarOpen('An error occurred while saving the data.', 'error');
                })
                .finally(() => {
                    setSaveButtonLoading(false);
                });
        } else {
            // Nothing pending — go straight to the report
            history.push({
                pathname: `/gst_computation_report_details_data/${reconId}`
            });
        }
    };


    // const handleComputationReport = () => {
    //     setSnackCloseTime(5000)
    //     if (!isSavedItc) {
    //         handleSnackbarOpen('Please save the ITC data before proceeding to the Computation Report.', 'success')
    //         return
    //     } else if (!isSavedRcm) {
    //         handleSnackbarOpen('Please save the RCM data before proceeding to the Computation Report.', 'success')
    //         return
    //     }

    //     history.push({
    //         pathname: `/gst_computation_report_details_data/${reconId}`
    //     })

    // }


    const getGstComputationDetailedData = () => {
        setLoading(true)
        const payLoadData = {
            business_entity_id: localStorage.getItem('business_entity_id'),
            summary_id: reconId,
            page: page + 1,
            limit: rowsPerPage,
            main_type: selectedOption,
            sub_type: value,
            search_gstin: filterValueGstin,
            search_legal_name: filterValueLegalName,
            search_document_number: filterValueDocumentNo,
            search_match_result: filterValueMatchResult,
            financial_year: financial_year || null,
                period: period || null,
               excel_export: false,
        }

        axios.post('/get_itc_exact_equal_computation_report_data', payLoadData)
            .then((response) => {
                if (response.status) {
                    setData(response.data.records);
                    setTotalRows(response.data.total);
                    setTaxPeriod(response.data.tax_period)
                    setItcUtilizedMonthOptions(response.data.itc_utilized_month_options)
                }
            })
            .catch((error) => {
                console.error('Error fetching data:', error);
            })
            .finally(() => {
                setLoading(false);
            });
    }

    const downloadBase64File = (contentType, base64Data, fileName) => {
        const linkSource = `data:${contentType};base64,${base64Data}`;
        const downloadLink = document.createElement("a");
        downloadLink.href = linkSource;
        downloadLink.download = fileName;
        downloadLink.click();
    };


    const handleItcDataSave = () => {
        if (!validateReversalData()) return;

        setSaveButtonLoading(true)
        const payLoadData = {
            business_entity_id: localStorage.getItem('business_entity_id'),
            summary_id: reconId,
            records: data,
            is_changed: isChngesNotSaved,
            financial_year: financial_year || null,
            period: period || null,
                   }
        axios.post('/save_itc_utilized_records', payLoadData)
            .then((res) => {
                if (res.status === 200 && res.data.status) {
                    setIsComputeEnabled(true)
                    setSnackCloseTime(1500)
                    handleSnackbarOpen(`${selectedOption} utilized records saved successfully.`, 'success')
                } else {
                    setIsComputeEnabled(false)
                    handleSnackbarOpen('Save failed. Please try again.', 'error');
                }
            }).catch((err) => {
                setIsComputeEnabled(false)
                handleSnackbarOpen('An error occurred while saving the data.', 'error');
            }).finally(() => {
                setSaveButtonLoading(false)
                if (selectedOption === "ITC") {
                    setIsSaveItc(true)
                } else {
                    setIsSavedRcm(true)
                }
                satIsChnagesNotSaved(false)
            })
    }

    const handleExportExcel = () => {
        setExportButtonLaoding(true)
        const payLoadData = {
            'summary_id': reconId,
            'business_entity_id': localStorage.getItem("business_entity_id"),
        }
        const contentType =
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
        const filename = `GST Computation Report.xlsx`;

        axios.post('/computation_excel_report', payLoadData)
            .then((res) => {
                downloadBase64File(contentType, res?.data?.file, filename);
            }).catch((err) => {

            })
            .finally(() => {
                setExportButtonLaoding(false)
            })
    }

    // ---- Bulk Export handlers ----
    const handleBulkExportOpen = () => {
        setBulkExportOpen(true);
        setShowBulkLogs(true);
        fetchBulkExportLogs();
    };

    const handleBulkExportClose = () => {
        setBulkExportOpen(false);
        setBulkUploadFile(null);
        setBulkDragActive(false);
        setShowBulkLogs(false);
    };

    const handleBulkFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setBulkUploadFile(file);
            handleBulkFileUpload(file);
        }
    };

    const handleBulkDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setBulkDragActive(true);
    };

    const handleBulkDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setBulkDragActive(false);
    };

    const handleBulkDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setBulkDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            const file = e.dataTransfer.files[0];
            setBulkUploadFile(file);
            handleBulkFileUpload(file);
            e.dataTransfer.clearData();
        }
    };

    const handleBulkTemplateDownload = () => {
        setBulkTemplateLoading(true);

        const businessEntityId = localStorage.getItem('business_entity_id');

        const contentType =
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
        const filename = `Computation Upload Template.xlsx`;

        axios
            .get(`/computation_report_manual_reversal/${reconId}/${businessEntityId}`)
            .then((res) => {
                downloadBase64File(contentType, res?.data?.file, filename);
            })
            .catch((err) => {
                handleSnackbarOpen('Unable to download template. Please try again.', 'error');
            })
            .finally(() => {
                setBulkTemplateLoading(false);
            });
    };

    const handleBulkLogErrorDownload = (log) => {
        const log_id = log?.id;
        const bussinessEntityId = localStorage.getItem("business_entity_id");

        if (!log_id) return;

        const contentType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
        const filename = `${log.file_name || 'error'}_error.xlsx`;

        axios.get(`/computation_upload_excel_download/${log_id}/${bussinessEntityId}`)
            .then((res) => {
                downloadBase64File(contentType, res?.data?.file, filename);
            })
            .catch((err) => {
                handleSnackbarOpen('Unable to download error file. Please try again.', 'error');
            });
    };

    const fetchBulkExportLogs = () => {
        setBulkLogsLoading(true);
        axios.get(`/computation_report_manual_upload_logs/${reconId}`)
            .then((res) => {
                // API returns a plain array, not { records: [...] }
                const logs = Array.isArray(res?.data) ? res.data : (res?.data?.records || []);
                setBulkExportLogs(logs);
            })
            .catch((err) => {
                console.error('Error fetching bulk export logs:', err);
            })
            .finally(() => {
                setBulkLogsLoading(false);
            });
    };

    const handleBulkFileUpload = (file) => {
        const fileToUpload = file || bulkUploadFile;
        if (!fileToUpload) return;

        setBulkUploadLoading(true);

        const reader = new FileReader();
        reader.onload = () => {
            const base64String = reader.result;

            const payLoadData = {
                business_entity_id: localStorage.getItem('business_entity_id'),
                summary_id: reconId,
                file: base64String,
                document_name: fileToUpload.name,
            };

            axios.post('/computation_reversal_excel_upload', payLoadData)
                .then((res) => {
                    handleSnackbarOpen('File uploaded successfully.', 'success');
                    setShowBulkLogs(true);
                    fetchBulkExportLogs();
                })
                .catch((err) => {
                    handleSnackbarOpen('Upload failed. Please try again.', 'error');
                })
                .finally(() => {
                    setBulkUploadLoading(false);
                });
        };
        reader.onerror = () => {
            handleSnackbarOpen('Unable to read the file. Please try again.', 'error');
            setBulkUploadLoading(false);
        };
        reader.readAsDataURL(fileToUpload);
    };


    const numberFormat = (value) => {
        if (!value || value === null || value === "") {
            return "₹ 0";
        }

        const numberValue = parseFloat(value);

        if (!isNaN(numberValue)) {
            return new Intl.NumberFormat("en-IN", {
                style: "currency",
                currency: "INR",
                maximumSignificantDigits: 10,
            }).format(numberValue);
        } else {
            return "₹ 0";
        }
    };

    const renderFilterPopover = (anchorEl, column, filterValue, setFilterValue) => {
        return (
            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={() => handlePopoverClose(column)}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                PaperProps={{ sx: { borderRadius: '8px', boxShadow: '0 8px 24px rgba(15, 23, 42, 0.14)', border: `1px solid ${palette.border}` } }}
            >
                <TextField
                    value={filterValue}
                    onChange={(e) => handleFilterValueChange(column, e)}
                    placeholder={`Filter by ${column}`}
                    size="small"
                    autoFocus
                    sx={{
                        margin: '8px',
                        width: '190px',
                        '& .MuiOutlinedInput-root': {
                            fontSize: '13px',
                            borderRadius: '7px',
                            '& fieldset': { borderColor: palette.border },
                            '&:hover fieldset': { borderColor: palette.primary },
                            '&.Mui-focused fieldset': { borderColor: palette.primary, borderWidth: '1.5px' },
                        },
                    }}
                />
            </Popover>
        );
    };


    const columnHeaderSx = {
        textAlign: 'center',
        fontWeight: 700,
        fontSize: '11px',
        textTransform: 'none',
        letterSpacing: '0.3px',
        whiteSpace: 'nowrap',
        padding: '12px 8px',
        position: 'sticky',
        top: 0,
        zIndex: 1,
        // !important guards against a global MuiTableCell style override in the host app
        // washing this out to a near-invisible color, which is what the faded header was.
        color: `${palette.tableHeaderText} !important`,
        backgroundColor: `${palette.tableHeaderBg} !important`,
        borderBottom: 'none !important',
        opacity: '1 !important',
    };

    const bodyCellSx = {
        textAlign: 'center',
        padding: '11px 8px',
        fontSize: '12.5px',
        color: palette.inkSoft,
        whiteSpace: 'nowrap',
        border: 'none',
    };

    const numericCellSx = {
        ...bodyCellSx,
        fontFamily: '"Inter", sans-serif',
        fontVariantNumeric: 'tabular-nums',
    };

    // Row accent stripe — a thin colored bar keyed to that row's match result,
    // so status is readable at a glance from the left edge, before reading any cell.
    const getRowAccent = (row) => getStatusChipStyle(row?.match_result).dot;

    // Cycles through the label palette per row index, purely for the leading GSTIN pill —
    // mirrors the rotating colored labels in the reference table design.
    const getLabelColor = (index) => palette.labelCycle[index % palette.labelCycle.length];

    const DetailsTable = () => {
        return (
            <Paper sx={{
                width: '100%',
                overflow: 'hidden',
                marginTop: '10px',
                borderRadius: '14px',
                border: `1px solid ${palette.border}`,
                boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04), 0 8px 24px rgba(15, 23, 42, 0.05)',
            }}>
                <TableContainer sx={{
                    overflowX: 'auto',
                    maxHeight: '52vh',
                    backgroundColor: palette.tableBodyAltBg,
                    '&::-webkit-scrollbar': {
                        width: '6px',
                        height: '6px',
                    },
                    '&::-webkit-scrollbar-track': {
                        backgroundColor: 'transparent',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: palette.borderStrong,
                        borderRadius: '8px',
                    },
                    '&::-webkit-scrollbar-thumb:hover': {
                        backgroundColor: palette.textFaint,
                    },
                }}>
                    <Table sx={{ minWidth: '100%', borderCollapse: 'separate', borderSpacing: '0 8px', padding: '0 10px' }} size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ width: '12%', ...columnHeaderSx }}>
                                    <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                        <span>GSTIN</span>
                                        <IconButton
                                            onClick={(e) => handleFilterIconClick('gstin', e)}
                                            size="small"
                                            sx={{
                                                marginLeft: '2px',
                                                color: filterValueGstin ? '#ffffff' : 'rgba(255,255,255,0.6)'
                                            }}
                                        >
                                            <FilterAltIcon sx={{ fontSize: '14px' }} />
                                        </IconButton>
                                    </Box>
                                </TableCell>

                                <TableCell sx={{ width: '12%', ...columnHeaderSx }}>
                                    <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                        <span>Legal Name</span>
                                        <IconButton
                                            onClick={(e) => handleFilterIconClick('legalName', e)}
                                            size="small"
                                            sx={{
                                                marginLeft: '2px',
                                                color: filterValueLegalName ? '#ffffff' : 'rgba(255,255,255,0.6)'
                                            }}
                                        >
                                            <FilterAltIcon sx={{ fontSize: '14px' }} />
                                        </IconButton>
                                    </Box>
                                </TableCell>
                                <TableCell sx={{ width: '8%', ...columnHeaderSx }}>GST Invoice Type</TableCell>
                                <TableCell sx={{ width: '12%', ...columnHeaderSx }}>
                                    <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                        <span>Document No</span>
                                        <IconButton
                                            onClick={(e) => handleFilterIconClick('documentNo', e)}
                                            size="small"
                                            sx={{
                                                marginLeft: '2px',
                                                color: filterValueDocumentNo ? '#ffffff' : 'rgba(255,255,255,0.6)'
                                            }}
                                        >
                                            <FilterAltIcon sx={{ fontSize: '14px' }} />
                                        </IconButton>
                                    </Box>
                                </TableCell>
                                <TableCell sx={{ width: '8%', ...columnHeaderSx }}>Document Date</TableCell>
                                <TableCell sx={{ width: '10%', ...columnHeaderSx }}>Booking Date</TableCell>
                                <TableCell sx={{ width: '8%', ...columnHeaderSx }}>IGST</TableCell>
                                <TableCell sx={{ width: '8%', ...columnHeaderSx }}>CGST</TableCell>
                                <TableCell sx={{ width: '8%', ...columnHeaderSx }}>SGST</TableCell>
                                <TableCell sx={{ width: '8%', ...columnHeaderSx }}>Taxable Value</TableCell>
                                <TableCell sx={{ width: '8%', ...columnHeaderSx }}>Total GST</TableCell>
                                <TableCell sx={{ width: '8%', ...columnHeaderSx }}>Total Amount</TableCell>
                                <TableCell sx={{ width: '8%', ...columnHeaderSx }}>
                                    <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                        <span>Match Result</span>
                                        <IconButton
                                            onClick={(e) => handleFilterIconClick('matchResult', e)}
                                            size="small"
                                            sx={{
                                                marginLeft: '2px',
                                                color: filterValueMatchResult ? '#ffffff' : 'rgba(255,255,255,0.6)'
                                            }}
                                        >
                                            <FilterAltIcon sx={{ fontSize: '14px' }} />
                                        </IconButton>
                                    </Box>
                                </TableCell>
                                <TableCell sx={{ width: '8%', ...columnHeaderSx }}>RCM Status</TableCell>
                                <TableCell sx={{ width: '8%', ...columnHeaderSx }}>ITC Status</TableCell>
                                <TableCell sx={{ width: '10%', ...columnHeaderSx }}>ITC Available Month</TableCell>
                                <TableCell sx={{ width: '8%', ...columnHeaderSx }}>Tax Period</TableCell>
                                {showReversalColumns && (
                                    <>
                                        <TableCell sx={{ width: '10%', ...columnHeaderSx }}>ITC Utilized Month</TableCell>
                                        <TableCell sx={{ width: '10%', ...columnHeaderSx }}>Reversal</TableCell>
                                    </>
                                )}
                            </TableRow>
                        </TableHead>
                        <TableBody sx={{ overflowY: 'auto' }}>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={showReversalColumns ? 19 : 17} align="center" sx={{ padding: '28px' }}>
                                        <CircularProgress sx={{ color: palette.primary }} size={26} />
                                    </TableCell>
                                </TableRow>
                            ) : (data && data.length === 0) ? (
                                <TableRow>
                                    <TableCell colSpan={showReversalColumns ? 19 : 17} align="center" sx={{ padding: '28px', color: palette.textMuted, fontSize: '13px' }}>
                                        No data available
                                    </TableCell>
                                </TableRow>
                            ) : (
                                data?.map((row, index) => (
                                    <TableRow
                                        key={`${row.document_no}-${index}`}
                                        sx={{
                                            backgroundColor: index % 2 === 0 ? palette.tableBodyBg : palette.tableBodyAltBg,
                                            boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
                                            '&:hover': {
                                                backgroundColor: palette.tableBodyHover,
                                                boxShadow: '0 4px 12px rgba(47, 95, 189, 0.12)',
                                            },
                                            '& td:first-of-type': { borderTopLeftRadius: '10px', borderBottomLeftRadius: '10px' },
                                            '& td:last-of-type': { borderTopRightRadius: '10px', borderBottomRightRadius: '10px' },
                                            transition: 'background-color 0.15s ease, box-shadow 0.15s ease',
                                        }}
                                    >
                                        <TableCell sx={{ ...bodyCellSx, padding: '7px 8px' }}>
                                            <Chip
                                                size="small"
                                                label={row.cust_gstin}
                                                sx={{
                                                    height: '22px',
                                                    fontSize: '11.5px',
                                                    fontWeight: 700,
                                                    color: '#ffffff',
                                                    backgroundColor: getLabelColor(index),
                                                    borderRadius: '6px',
                                                    '& .MuiChip-label': { paddingLeft: '8px', paddingRight: '8px' },
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell sx={bodyCellSx}>{row.cust_legal_name}</TableCell>
                                        <TableCell sx={bodyCellSx}>{row.gst_invoice_type}</TableCell>
                                        <TableCell sx={{ ...bodyCellSx, fontWeight: 500 }}>{row.document_no}</TableCell>
                                        <TableCell sx={{ ...bodyCellSx, color: palette.textMuted }}>{row.document_date}</TableCell>
                                        <TableCell sx={{ ...bodyCellSx, color: palette.textMuted }}>{row.booking_date}</TableCell>
                                        <TableCell sx={numericCellSx}>{numberFormat(row.igst)}</TableCell>
                                        <TableCell sx={numericCellSx}>{numberFormat(row.cgst)}</TableCell>
                                        <TableCell sx={numericCellSx}>{numberFormat(row.sgst)}</TableCell>
                                        <TableCell sx={numericCellSx}>{numberFormat(row.taxable_value)}</TableCell>
                                        <TableCell sx={numericCellSx}>{numberFormat(row.total_gst)}</TableCell>
                                        <TableCell sx={{ ...numericCellSx, fontWeight: 700, color: palette.ink, fontSize: '13px' }}>{numberFormat(row.total_amount)}</TableCell>
                                        <TableCell sx={{ ...bodyCellSx, padding: '7px 8px' }}><StatusChip value={row.match_result} /></TableCell>
                                        <TableCell sx={{ ...bodyCellSx, padding: '7px 8px' }}><StatusChip value={row.final_rcm_status} /></TableCell>
                                        <TableCell sx={{ ...bodyCellSx, padding: '7px 8px' }}><StatusChip value={row.final_itc_status} /></TableCell>
                                        <TableCell sx={{ ...bodyCellSx, color: palette.textMuted }}>{row.itc_available_month}</TableCell>
                                        <TableCell sx={{ ...bodyCellSx, color: palette.textMuted }}>{row.tax_period}</TableCell>
                                        {showReversalColumns && (
                                            <>
                                                <TableCell sx={{ textAlign: 'center', padding: '6px 8px', border: 'none', verticalAlign: 'middle' }}>
                                                    {canWrite("GST COMPUTATION REPORT") ? (
                                                        <FormControl sx={{ maxWidth: 116, height: '24px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                                            <Select
                                                                value={row.itc_utilized_month ? row.itc_utilized_month : "None"}
                                                                onChange={(e) => handleItcMonthChange(e, index)}
                                                                displayEmpty
                                                                sx={{
                                                                    backgroundColor: '#ffffff',
                                                                    fontSize: '12px',
                                                                    height: '28px',
                                                                    padding: '0 6px',
                                                                    borderRadius: '6px',
                                                                    border: `1px solid ${palette.tableBorder}`,
                                                                    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                                                                    '&:hover': { borderColor: palette.tableHeaderAccent, backgroundColor: palette.tableBodyHover },
                                                                    '&.Mui-focused': { borderColor: palette.tableHeaderAccent, backgroundColor: '#ffffff' },
                                                                }}
                                                            >
                                                                {row.block_utilized == "Y" ? (
                                                                    <MenuItem key={row.itc_utilized_month} value={row.itc_utilized_month}>
                                                                        {row.itc_utilized_month}
                                                                    </MenuItem>
                                                                ) : (
                                                                    itcUtilizedMonthOptions.map((month) => (
                                                                        <MenuItem key={month.id} value={month.month}>
                                                                            {month.month}
                                                                        </MenuItem>
                                                                    ))
                                                                )}
                                                            </Select>
                                                        </FormControl>
                                                    ) : (
                                                        <span style={{ fontSize: '12.5px', color: palette.textMuted }}>{row.itc_utilized_month || 'N/A'}</span>
                                                    )}
                                                </TableCell>
                                                <TableCell sx={{ textAlign: 'center', padding: '6px 8px', border: 'none', verticalAlign: 'middle' }}>
                                                    {canWrite("GST COMPUTATION REPORT") ? (
                                                        <FormControl sx={{ minWidth: 132 }}>
                                                            <Select
                                                                value={row.perm_and_temp_rev || ''}
                                                                onChange={(e) => handleReversalChange(e, index)}
                                                                displayEmpty
                                                                sx={{
                                                                    fontSize: '12px',
                                                                    height: '28px',
                                                                    borderRadius: '6px',
                                                                    border: `1px solid ${row.perm_and_temp_rev === 'Permanent Rev.' ? palette.amberBorder : palette.tableBorder}`,
                                                                    backgroundColor: row.perm_and_temp_rev === 'Permanent Rev.' ? palette.amberSoft : '#ffffff',
                                                                    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                                                                    '&:hover': { backgroundColor: row.perm_and_temp_rev === 'Permanent Rev.' ? palette.amberSoft : palette.tableBodyHover },
                                                                }}
                                                            >
                                                                <MenuItem value="Permanent Rev.">Permanent Rev.</MenuItem>
                                                                <MenuItem value="Temporary Rev.">Temporary Rev.</MenuItem>
                                                                <MenuItem value="">None</MenuItem>
                                                            </Select>
                                                        </FormControl>
                                                    ) : (
                                                        <span style={{ fontSize: '12.5px', color: palette.textMuted }}>{row.perm_and_temp_rev || 'N/A'}</span>
                                                    )}
                                                </TableCell>
                                            </>
                                        )}
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    component="div"
                    count={totalRows}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    sx={{
                        backgroundColor: palette.card,
                        borderTop: `1px solid ${palette.tableBorder}`,
                        position: 'sticky',
                        bottom: 0,
                        minHeight: '42px',
                        color: palette.inkSoft,
                        fontSize: '12.5px',
                        '& .MuiToolbar-root': { minHeight: '42px' },
                        '& .MuiTablePagination-selectIcon': {
                            color: palette.tableHeaderBg,
                        },
                        '& .MuiTablePagination-actions button': {
                            color: palette.tableHeaderBg,
                        },
                    }}
                />
            </Paper>
        );
    };



    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
                .gst-recon-wrap, .gst-recon-wrap * {
                    font-family: 'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif !important;
                }
                /* Popover/Dialog/Select menus render via a portal at the end of
                   document.body, outside .gst-recon-wrap, so they need their own rule. */
                .MuiPopover-root, .MuiPopover-root *,
                .MuiDialog-root, .MuiDialog-root *,
                .MuiModal-root, .MuiModal-root * {
                    font-family: 'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif !important;
                }
            `}</style>
            <Box className="gst-recon-wrap" sx={{ width: '100%' }}>

                <Box sx={{
                    marginBottom: '16px',
                    backgroundColor: palette.card,
                    borderRadius: '14px',
                    border: `1px solid ${palette.border}`,
                    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04), 0 4px 10px rgba(15, 23, 42, 0.05)',
                    overflow: 'hidden',
                }}>
                    {/* Row 1 — identity + primary actions */}
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '14px 18px',
                    }}>
                        <Tooltip title="Back" arrow>
                            <IconButton
                                onClick={() => history.goBack()}
                                size="small"
                                sx={{
                                    borderRadius: '9px',
                                    padding: '7px',
                                    color: palette.inkSoft,
                                    backgroundColor: palette.slateSoft,
                                    border: `1px solid ${palette.border}`,
                                    '&:hover': { backgroundColor: '#EEF2FA' },
                                }}
                            >
                                <ArrowBackIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>

                        <Box sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '36px',
                            height: '36px',
                            borderRadius: '10px',
                            backgroundColor: palette.primarySoft,
                            border: `1px solid ${palette.primaryBorder}`,
                            flexShrink: 0,
                        }}>
                            <BubbleChartIcon sx={{ fontSize: '1.1rem', color: palette.primary }} />
                        </Box>

                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                            <Typography sx={{
                                fontWeight: 700,
                                fontSize: '16px',
                                color: palette.ink,
                                lineHeight: 1.3,
                                fontFamily: '"Inter", sans-serif',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }}>
                                GST Computation Detailed Report
                            </Typography>
                            <Typography sx={{
                                fontSize: '12px',
                                fontWeight: 500,
                                color: palette.textMuted,
                                letterSpacing: '0.2px',
                            }}>
                                {financial_year && period
                                    ? `${financial_year}  ·  ${period}`
                                    : taxPeriod
                                        ? `Tax Period · ${taxPeriod}`
                                        : '—'}
                            </Typography>
                        </Box>

                        <Tooltip title="Bulk Export" arrow>
                            <IconButton
                                onClick={handleBulkExportOpen}
                                size="small"
                                sx={{
                                    borderRadius: '9px',
                                    padding: '7px',
                                    color: palette.inkSoft,
                                    backgroundColor: palette.slateSoft,
                                    border: `1px solid ${palette.border}`,
                                    '&:hover': { backgroundColor: '#EEF2FA' },
                                }}
                            >
                                <UploadFileIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>

                        {canWrite("GST COMPUTATION REPORT") && (
                            <>
                                <Tooltip title={`Confirm ${selectedOption}`} arrow>
                                    <Button
                                        disableRipple
                                        onClick={() => { if (!saveButtonLoading) handleItcDataSave(); }}
                                        startIcon={saveButtonLoading ? <CircularProgress sx={{ color: '#ffffff' }} size={14} /> : <SaveIcon fontSize="small" />}
                                        sx={{
                                            textTransform: 'none',
                                            borderRadius: '9px',
                                            padding: '7px 16px',
                                            fontSize: '12.5px',
                                            fontWeight: 700,
                                            color: '#ffffff',
                                            backgroundColor: palette.primary,
                                            boxShadow: 'none',
                                            '&:hover': { backgroundColor: palette.primaryHover },
                                        }}
                                    >
                                        Save
                                    </Button>
                                </Tooltip>

                                <Tooltip title={isComputeEnabled ? "Computation" : "Save the data to enable Compute"} arrow>
                                    <span>
                                        <Button
                                            disableRipple
                                            onClick={handleComputationReport}
                                            disabled={!isComputeEnabled || saveButtonLoading}
                                            startIcon={<AnalyticsIcon fontSize="small" />}
                                            sx={{
                                                textTransform: 'none',
                                                borderRadius: '9px',
                                                padding: '7px 16px',
                                                fontSize: '12.5px',
                                                fontWeight: 700,
                                                color: palette.primary,
                                                backgroundColor: palette.primarySoft,
                                                border: `1px solid ${palette.primaryBorder}`,
                                                '&:hover': { backgroundColor: '#E0E4FE' },
                                                '&.Mui-disabled': {
                                                    color: palette.textFaint,
                                                    backgroundColor: palette.slateSoft,
                                                    border: `1px solid ${palette.border}`,
                                                },
                                            }}
                                        >
                                            Compute
                                        </Button>
                                    </span>
                                </Tooltip>
                            </>
                        )}
                    </Box>

                    {/* Row 2 — ITC/RCM switch on the left, Matched/Missing/Mismatch pinned to the right corner, same row */}
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: '10px',
                        padding: '10px 18px',
                        borderTop: `1px solid ${palette.border}`,
                        backgroundColor: palette.slateSoft,
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography sx={{ fontSize: '11px', fontWeight: 700, color: palette.textFaint, textTransform: 'uppercase', letterSpacing: '0.6px', marginRight: '10px' }}>
                                Report Type
                            </Typography>
                            <FormControl sx={{ width: 'auto' }}>
                                <RadioGroup
                                    aria-label="report-option"
                                    name="report-option"
                                    value={selectedOption}
                                    onChange={handleSelectChange}
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        gap: '2px',
                                        padding: '3px',
                                        backgroundColor: palette.card,
                                        border: `1px solid ${palette.border}`,
                                        borderRadius: '9px',
                                    }}
                                >
                                    <FormControlLabel
                                        value="ITC"
                                        control={<BpRadio sx={{ display: 'none' }} />}
                                        label="ITC"
                                        onClick={() => setSelectedOption('ITC')}
                                        sx={{
                                            margin: 0,
                                            borderRadius: '7px',
                                            padding: '5px 16px',
                                            backgroundColor: selectedOption === 'ITC' ? palette.primary : 'transparent',
                                            transition: 'background-color 0.2s ease',
                                            '& .MuiFormControlLabel-label': {
                                                fontSize: '12px',
                                                fontWeight: '700',
                                                color: selectedOption === 'ITC' ? '#ffffff' : palette.textMuted,
                                                letterSpacing: '0.4px',
                                            },
                                        }}
                                    />
                                    <FormControlLabel
                                        value="RCM"
                                        control={<BpRadio sx={{ display: 'none' }} />}
                                        label="RCM"
                                        onClick={() => setSelectedOption('RCM')}
                                        sx={{
                                            margin: 0,
                                            borderRadius: '7px',
                                            padding: '5px 16px',
                                            backgroundColor: selectedOption === 'RCM' ? palette.primary : 'transparent',
                                            transition: 'background-color 0.2s ease',
                                            '& .MuiFormControlLabel-label': {
                                                fontSize: '12px',
                                                fontWeight: '700',
                                                color: selectedOption === 'RCM' ? '#ffffff' : palette.textMuted,
                                                letterSpacing: '0.4px',
                                            },
                                        }}
                                    />
                                </RadioGroup>
                            </FormControl>
                        </Box>

                        {/* Matched / Missing / Mismatch — same switch style as ITC / RCM */}
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {/* <Typography sx={{ fontSize: '11px', fontWeight: 700, color: palette.textFaint, textTransform: 'uppercase', letterSpacing: '0.6px', marginRight: '10px' }}>
                                Status
                            </Typography> */}
                            <FormControl sx={{ width: 'auto' }}>
                                <RadioGroup
                                    aria-label="status-option"
                                    name="status-option"
                                    value={value}
                                    onChange={(e) => handleChangeTab(null, e.target.value)}
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        gap: '2px',
                                        padding: '3px',
                                        backgroundColor: palette.card,
                                        border: `1px solid ${palette.border}`,
                                        borderRadius: '9px',
                                    }}
                                >
                                    {['Matched', 'Missing', 'Mismatch'].map((tab) => (
                                        <FormControlLabel
                                            key={tab}
                                            value={tab}
                                            control={<BpRadio sx={{ display: 'none' }} />}
                                            label={tab}
                                            onClick={() => handleChangeTab(null, tab)}
                                            sx={{
                                                margin: 0,
                                                borderRadius: '7px',
                                                padding: '5px 16px',
                                                backgroundColor: value === tab ? palette.primary : 'transparent',
                                                transition: 'background-color 0.2s ease',
                                                '& .MuiFormControlLabel-label': {
                                                    fontSize: '12px',
                                                    fontWeight: '700',
                                                    color: value === tab ? '#ffffff' : palette.textMuted,
                                                    letterSpacing: '0.4px',
                                                },
                                            }}
                                        />
                                    ))}
                                </RadioGroup>
                            </FormControl>
                        </Box>
                    </Box>
                </Box>

                {/* Table Component */}
                {DetailsTable()}
                <Snackbar
                    anchorOrigin={{
                        vertical: "top",
                        horizontal: "right",
                    }}
                    open={snackbarOpen}
                    autoHideDuration={snackCloseTime}
                    onClose={handleSnackbarClose}
                    style={{ maxWidth: '25%' }}
                >
                    <MuiAlert
                        elevation={6}
                        variant="filled"
                        onClose={handleSnackbarClose}
                        severity={snackbarSeverity}
                    >
                        {responseMessage}
                        {message}
                    </MuiAlert>
                </Snackbar>
            </Box>
            {renderFilterPopover(anchorElGstin, 'gstin', filterValueGstin, setFilterValueGstin)}
            {renderFilterPopover(anchorElLegalName, 'legalName', filterValueLegalName, setFilterValueLegalName)}
            {renderFilterPopover(anchorElDocumentNo, 'documentNo', filterValueDocumentNo, setFilterValueDocumentNo)}
            {renderFilterPopover(anchorElMatchResult, 'matchResult', filterValueMatchResult, setFilterValueMatchResult)}

            {/* Bulk Export Dialog */}
            <Dialog
                open={bulkExportOpen}
                onClose={handleBulkExportClose}
                maxWidth="md"
                fullWidth
                PaperProps={{ sx: { borderRadius: '14px', overflow: 'hidden', border: `1px solid ${palette.border}` } }}
            >
                {/* Header */}
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '14px 18px',
                    backgroundColor: palette.card,
                    borderBottom: `1px solid ${palette.border}`,
                }}>
                    <Typography sx={{ fontSize: '15px', fontWeight: 700, color: palette.ink, letterSpacing: '0.2px' }}>
                        Bulk Upload
                    </Typography>
                    <IconButton onClick={handleBulkExportClose} size="small" sx={{ color: palette.textMuted, '&:hover': { color: palette.ink, backgroundColor: palette.slateSoft } }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>

                <DialogContent sx={{ padding: 0 }}>
                    <Box sx={{ display: 'flex', minHeight: '300px' }}>

                        {/* Left panel - Drop zone */}
                        <Box sx={{
                            width: '38%',
                            backgroundColor: palette.slateSoft,
                            padding: '16px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            borderRight: `1px solid ${palette.border}`,
                        }}>
                            <Typography sx={{
                                fontSize: '11px',
                                fontWeight: 700,
                                letterSpacing: '0.8px',
                                color: palette.textMuted,
                                textTransform: 'uppercase',
                                alignSelf: 'flex-start',
                                marginBottom: '10px',
                            }}>
                                Upload Bulk Data
                            </Typography>

                            <Box
                                component="label"
                                onDragOver={handleBulkDragOver}
                                onDragLeave={handleBulkDragLeave}
                                onDrop={handleBulkDrop}
                                sx={{
                                    width: '100%',
                                    flexGrow: 1,
                                    minHeight: '200px',
                                    border: `1.5px dashed ${bulkDragActive ? palette.primary : palette.borderStrong}`,
                                    borderRadius: '10px',
                                    backgroundColor: bulkDragActive ? palette.primarySoft : palette.card,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: bulkUploadLoading ? 'not-allowed' : 'pointer',
                                    opacity: bulkUploadLoading ? 0.6 : 1,
                                    transition: 'all 0.2s ease',
                                    textAlign: 'center',
                                    padding: '10px',
                                }}
                            >
                                <input type="file" hidden accept=".xlsx" onChange={handleBulkFileChange} disabled={bulkUploadLoading} />
                                {bulkUploadLoading ? (
                                    <CircularProgress size={28} sx={{ marginBottom: '8px', color: palette.primary }} />
                                ) : (
                                    <CloudUploadOutlinedIcon sx={{ fontSize: 34, color: palette.primary, marginBottom: '8px' }} />
                                )}
                                <Typography sx={{ fontWeight: 600, fontSize: '14px', color: palette.ink }}>
                                    {bulkUploadLoading ? 'Uploading...' : 'Drop your files here'}
                                </Typography>
                                {!bulkUploadLoading && (
                                    <Typography sx={{ fontSize: '12px', color: palette.textMuted, marginTop: '2px' }}>
                                        or <span style={{ color: palette.primary, fontWeight: 600 }}>Browse</span>
                                    </Typography>
                                )}
                                <Typography sx={{ fontSize: '11px', color: palette.textFaint, marginTop: '4px' }}>
                                    .xlsx only
                                </Typography>
                                {bulkUploadFile && !bulkUploadLoading && (
                                    <Typography sx={{ fontSize: '12px', color: palette.primary, marginTop: '6px', fontWeight: 500 }}>
                                        {bulkUploadFile.name}
                                    </Typography>
                                )}
                            </Box>
                        </Box>

                        {/* Right panel - Logs */}
                        <Box sx={{ width: '62%', padding: '16px', display: 'flex', flexDirection: 'column' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                                <Typography sx={{
                                    fontSize: '11px',
                                    fontWeight: 700,
                                    letterSpacing: '0.8px',
                                    color: palette.textMuted,
                                    textTransform: 'uppercase',
                                }}>
                                    Upload Logs
                                </Typography>

                                <Box sx={{ display: 'flex', gap: '6px' }}>
                                    <Button
                                        onClick={handleBulkTemplateDownload}
                                        disabled={bulkTemplateLoading}
                                        startIcon={bulkTemplateLoading ? <CircularProgress size={12} /> : <FileDownloadOutlinedIcon fontSize="small" />}
                                        variant="outlined"
                                        size="small"
                                        sx={{
                                            textTransform: 'none',
                                            borderRadius: '7px',
                                            fontSize: '12px',
                                            fontWeight: 600,
                                            padding: '4px 12px',
                                            borderColor: palette.primaryBorder,
                                            color: palette.primary,
                                            backgroundColor: palette.primarySoft,
                                            '&:hover': { backgroundColor: '#E0E4FE', borderColor: palette.primary },
                                        }}
                                    >
                                        Download Template
                                    </Button>
                                    <Button
                                        onClick={fetchBulkExportLogs}
                                        startIcon={<RefreshIcon fontSize="small" />}
                                        variant="outlined"
                                        size="small"
                                        sx={{
                                            textTransform: 'none',
                                            borderRadius: '7px',
                                            fontSize: '12px',
                                            fontWeight: 600,
                                            padding: '4px 12px',
                                            borderColor: palette.border,
                                            color: palette.inkSoft,
                                            backgroundColor: palette.slateSoft,
                                            '&:hover': { backgroundColor: '#E2E8F0', borderColor: palette.borderStrong },
                                        }}
                                    >
                                        Refresh
                                    </Button>
                                </Box>
                            </Box>

                            <TableContainer sx={{
                                flexGrow: 1,
                                maxHeight: '260px',
                                border: `1px solid ${palette.border}`,
                                borderRadius: '10px',
                            }}>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            {['Start Time', 'Status', 'Error'].map((label) => (
                                                <TableCell
                                                    key={label}
                                                    sx={{
                                                        backgroundColor: palette.slateSoft,
                                                        fontWeight: 700,
                                                        fontSize: '10.5px',
                                                        color: palette.inkSoft,
                                                        textTransform: 'uppercase',
                                                        letterSpacing: '0.6px',
                                                        borderBottom: `2px solid ${palette.primary}`,
                                                        padding: '8px 10px',
                                                    }}
                                                >
                                                    {label}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {bulkLogsLoading ? (
                                            <TableRow>
                                                <TableCell colSpan={3} align="center" sx={{ padding: '18px' }}>
                                                    <CircularProgress size={18} sx={{ color: palette.primary }} />
                                                </TableCell>
                                            </TableRow>
                                        ) : bulkExportLogs.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={3} align="center" sx={{ padding: '18px', color: palette.textFaint }}>
                                                    No logs available
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            bulkExportLogs.map((log, idx) => {
                                                const status = (log.upload_status || '').toLowerCase().trim();
                                                const isFailed = status === 'fail';
                                                const isSuccess = status === 'pass';
                                                const isInProgress = status === 'in-progress';
                                                const isInitiated = status === 'initiated';

                                                const statusStyle = isFailed
                                                    ? { bg: palette.dangerSoft, text: palette.danger, border: palette.dangerBorder }
                                                    : isSuccess
                                                        ? { bg: palette.successSoft, text: palette.success, border: palette.successBorder }
                                                        : isInProgress
                                                            ? { bg: palette.primarySoft, text: palette.primary, border: palette.primaryBorder }
                                                            : isInitiated
                                                                ? { bg: palette.amberSoft, text: palette.amber, border: palette.amberBorder }
                                                                : { bg: palette.slateSoft, text: palette.inkSoft, border: palette.border };

                                                // fail_reason is a JSON string like [{"row":2,"error_msg":"..."}] — parse for tooltip
                                                let failTooltip = 'Failed';
                                                if (isFailed && log.fail_reason) {
                                                    try {
                                                        const parsed = JSON.parse(log.fail_reason);
                                                        failTooltip = parsed
                                                            .map((e) => `Row ${e.row}: ${e.error_msg}`)
                                                            .join('\n');
                                                    } catch (e) {
                                                        failTooltip = log.fail_reason;
                                                    }
                                                }

                                                return (
                                                    <TableRow
                                                        key={log.id ?? idx}
                                                        sx={{
                                                            backgroundColor: palette.card,
                                                            transition: 'background-color 0.2s ease',
                                                            '&:hover': { backgroundColor: palette.primarySoft },
                                                        }}
                                                    >
                                                        <TableCell sx={{ fontSize: '12px', color: palette.textMuted, padding: '8px 10px', borderBottom: `1px solid ${palette.border}` }}>
                                                            {log.uploaded_date_time ? new Date(log.uploaded_date_time).toLocaleString() : '-'}
                                                        </TableCell>
                                                        <TableCell sx={{ padding: '8px 10px', borderBottom: `1px solid ${palette.border}` }}>
                                                            <Chip
                                                                size="small"
                                                                label={log.upload_status}
                                                                sx={{
                                                                    height: '20px',
                                                                    fontSize: '11px',
                                                                    fontWeight: 600,
                                                                    backgroundColor: statusStyle.bg,
                                                                    color: statusStyle.text,
                                                                    border: `1px solid ${statusStyle.border}`,
                                                                    borderRadius: '999px',
                                                                }}
                                                            />
                                                        </TableCell>
                                                        <TableCell sx={{ padding: '8px 10px', borderBottom: `1px solid ${palette.border}` }}>
                                                            <Tooltip
                                                                title={isFailed ? failTooltip : ''}
                                                                arrow
                                                                componentsProps={{ tooltip: { sx: { whiteSpace: 'pre-line' } } }}
                                                            >
                                                                <span>
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => handleBulkLogErrorDownload(log)}
                                                                        disabled={!isFailed || downloadingLogId === log.id}
                                                                        sx={{
                                                                            color: isFailed ? palette.danger : palette.textFaint,
                                                                        }}
                                                                    >
                                                                        {downloadingLogId === log.id
                                                                            ? <CircularProgress size={14} sx={{ color: palette.danger }} />
                                                                            : <FileDownloadOutlinedIcon fontSize="small" />}
                                                                    </IconButton>
                                                                </span>
                                                            </Tooltip>
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    </Box>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default GSTComputationReportDetails;